import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { apiClient } from '../../utils/apiClient';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const UserReservationConfirmPage: React.FC = () => {
  const navigate = useNavigate();
  const q = useQuery();
  const date = q.get('date') || '';
  const time = q.get('time') || '';
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  function toUtcIso(dateLabel: string, timeLabel: string): { start: string; end: string } {
    const [y, m, d] = dateLabel.split('.').map((v) => parseInt(v, 10));
    const [hh, mm] = timeLabel.split(':').map((v) => parseInt(v, 10));
    // 入力はローカル時間（例: JST）として解釈し、そのローカル時間をUTCへ変換
    const localStart = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
    const startIsoUtc = localStart.toISOString();
    const endIsoUtc = new Date(localStart.getTime() + 30 * 60 * 1000).toISOString();
    return { start: startIsoUtc, end: endIsoUtc };
  }

  const handleOk = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const { start, end } = toUtcIso(date, time);
      const body = {
        patientId: 'pat-0001',
        doctorId: 'doc-0001',
        slotStart: start,
        slotEnd: end,
      };
      const ap = await apiClient('/api/v1/appointments', {
        method: 'POST',
        body,
      });
      // 予約と同時にオンライン会議を発行（存在すれば取得）
      if (ap && ap.id) {
        try {
          await apiClient(`/api/v1/meetings/${encodeURIComponent(ap.id)}`, {
            method: 'POST',
          });
        } catch (e) {
          // ミーティング発行失敗は予約成立を優先し、UIでは非致命扱い
          console.warn('Failed to create meeting for appointment:', ap.id, e);
        }
      }
      navigate('/dashboard');
    } catch (e) {
      const status = (e as { status?: number })?.status;
      const msg =
        status === 409
          ? 'その時間帯は既に予約があります。別の枠をお選びください。'
          : '予約登録に失敗しました';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="bg-white flex flex-col gap-[40px] items-center px-[40px] size-full"
      aria-label="予約確認ページ"
    >
      {/* Header: 予約ページと同一デザイン */}
      <header className="bg-[#001973] flex items-center gap-[24px] px-[16px] py-[24px] w-full">
        <div className="flex items-center gap-[4px]">
          <img
            src="/figma_asset/HeaderIcon.svg"
            alt="SASオンラインロゴ"
            className="w-[40px] h-[27px]"
          />
          <p className="text-[18px] text-[#f5f5f5]">SAS オンライン</p>
        </div>
        <div className="ml-auto">
          <button className="bg-[#c8c8c8] px-[15px] py-[10px] rounded-[30px] text-[12px] text-black">
            ログアウト
          </button>
        </div>
      </header>

      {/* 顧客名 + アイコン類（簡易再現）*/}
      <div className="flex items-start justify-center w-full gap-[10px]">
        <div className="w-[275px] h-[29px] relative">
          <p className="absolute left-0 top-0 text-[22px] font-bold text-black">鈴木 太郎 様</p>
        </div>
        <div className="flex items-center gap-[12px]">
          <div className="border-2 border-[#b3b3b3] rounded-[32px] w-[33px] h-[33px] flex items-center justify-center">
            <div className="w-[20px] h-[20px]" />
          </div>
          <div className="border-2 border-[#b3b3b3] rounded-[32px] w-[33px] h-[33px] flex items-center justify-center">
            <div className="w-[20px] h-[20px]" />
          </div>
        </div>
      </div>

      {/* こちらでよろしいですか？ */}
      <div className="flex items-center justify-center w-full">
        <p className="text-[20px]">こちらでよろしいですか？</p>
      </div>

      {/* 確認カード */}
      <div className="w-full flex items-center justify-center">
        <div className="border border-black rounded-[20px] px-[30px] py-[40px] flex flex-col items-center gap-[40px]">
          <p className="text-[26px] font-bold text-center w-full">ご予約確認</p>
          <div className="flex items-center justify-center gap-[20px] text-[24px] font-bold">
            <p>PSG検査</p>
            <p>
              {date && time
                ? `${date.replace(/\./g, '/').replace(/^\d{4}\//, '')} ${time}`
                : '10/3 10:35'}
            </p>
          </div>
          <div className="flex items-center justify-center gap-[20px]">
            <button
              className="bg-[#808080] text-white rounded-[70px] px-[20px] py-[20px]"
              onClick={() => navigate(-1)}
            >
              ← やり直す
            </button>
            <button
              className={`rounded-[70px] px-[20px] py-[20px] ${submitting ? 'bg-[#ffb5b5] text-white' : 'bg-[#ff7a7a] text-white'}`}
              onClick={handleOk}
              disabled={submitting}
            >
              {submitting ? '登録中…' : 'OK →'}
            </button>
          </div>
          {error && <div className="text-[#ff7a7a] text-[14px] text-center">{error}</div>}
        </div>
      </div>
    </div>
  );
};

export default UserReservationConfirmPage;
